import type { Analytics, AnalyticsKpis, AnalyticsPeriod } from "../../api";

/**
 * Determinik insaytlar — "E'tibor berish kerak".
 *
 * QOIDALAR, bashorat emas: har bir insayt shu sahifada KO'RSATILGAN raqamdan
 * kelib chiqadi va foydalanuvchi uni qo'lda tekshira oladi. LLM yo'q, tasodif
 * yo'q, `Date.now()` yo'q — bir xil kirish har doim bir xil natija beradi.
 *
 * Har qoidada MUHIMLIK QOROVULI bor: kichik maxrajda foiz o'ynab ketadi
 * ("3 ta ishdan bittasi bekor bo'ldi → o'rtacha chek 40% tushdi" — bu xabar
 * emas, shovqin). Qoida qorovulidan o'tmasa umuman chiqmaydi; hech biri
 * o'tmasa butun blok chizilmaydi (bo'sh "hammasi joyida" kartasi ham yo'q).
 */

export const INSIGHT_THRESHOLDS = {
  /** Sof tushum o'zgarishi shu foizdan oshsa. */
  revenueChangePct: 20,
  /** Tushum qoidalari uchun eng kam hujjat soni (joriy + o'tgan). */
  minWorksForRevenue: 10,
  /** Qaytarishlar tushumning shu ulushidan oshsa. */
  returnsSharePct: 5,
  /** Kichik davrlarda foiz o'ynamasligi uchun eng kam tushum. */
  minRevenue: 5_000_000,
  /** Foyda ulushi shu FOIZ PUNKTiga tushsa. */
  marginDropPoints: 5,
  /** O'rtacha chek shu foizga tushsa. */
  avgCheckDropPct: 10,
  /** O'rtacha chek qoidasi uchun har ikki davrda eng kam ish soni. */
  minWorksForAvgCheck: 10,
  /** Bitta xizmat xizmat tushumining shu ulushidan ko'pini bersa. */
  serviceDominancePct: 40,
  /** Bitta xodim daromadning shu ulushidan ko'pini bersa. */
  employeeConcentrationPct: 50,
  /** Bitta mijoz xizmat tushumining shu ulushidan ko'pini bersa. */
  clientConcentrationPct: 20,
  /** Yangi mijoz to'xtaganini aytish uchun o'tgan davrdagi eng kam son. */
  minPrevNewClients: 5,
  /** Yuborilmagan eslatmalar ulushi. */
  reminderFailPct: 20,
  minReminders: 5,
} as const;

export type InsightSeverity = "danger" | "warning" | "info";

export type Insight = {
  id: string;
  severity: InsightSeverity;
  /** analytics.json → insights.<messageKey> */
  messageKey: string;
  params: Record<string, string | number>;
  /**
   * Ta'rifi noaniq o'lchovlar uchun ogohlantirish kaliti — chipning native
   * `title` atributiga tushadi (`InfoTooltip` EMAS: u <button>, chip esa
   * ba'zan o'zi <button> — ichma-ich tugma yaroqsiz).
   */
  hintKey?: string;
  /** Ichki tartiblash uchun kattalik (0..1+). */
  score: number;
};

const num = (v: string) => Number(v) || 0;
const RANK: Record<InsightSeverity, number> = { danger: 3, warning: 2, info: 1 };

export function buildInsights({
  kpis,
  analytics,
  hasProducts,
  period,
  limit = 3,
}: {
  kpis: AnalyticsKpis | null;
  analytics: Analytics | null;
  hasProducts: boolean;
  period: AnalyticsPeriod;
  limit?: number;
}): Insight[] {
  if (!kpis || !analytics) return [];
  const cur = kpis.current;
  const prev = kpis.previous;
  // Bo'sh davr — aytadigan gap yo'q (bo'sh holat kartalarning o'z ishi).
  if (num(cur.total_revenue) === 0 && cur.services_count === 0) return [];

  const out: Insight[] = [];
  const T = INSIGHT_THRESHOLDS;
  const curRevenue = num(cur.total_revenue);
  const servicesRevenue = num(cur.services_revenue);

  // ===== Sof tushum sezilarli o'zgardi =====
  const netPct = kpis.change_pct.net_revenue;
  if (
    netPct != null &&
    Math.abs(netPct) >= T.revenueChangePct &&
    cur.services_count + prev.services_count >= T.minWorksForRevenue
  ) {
    const dropped = netPct < 0;
    out.push({
      id: dropped ? "revenueDrop" : "revenueJump",
      severity: dropped ? "warning" : "info",
      messageKey: dropped ? "revenueDrop" : "revenueJump",
      params: { pct: Math.abs(netPct).toFixed(0) },
      score: Math.abs(netPct) / 100,
    });
  }

  // ===== Qaytarishlar tushumni yeyapti (faqat ombor bor tarifda) =====
  if (hasProducts && curRevenue >= T.minRevenue) {
    const share = (num(cur.returns_value) / curRevenue) * 100;
    if (share >= T.returnsSharePct) {
      out.push({
        id: "returnsShare",
        severity: "warning",
        messageKey: "returnsShare",
        params: { pct: share.toFixed(1) },
        hintKey: "returns",
        score: share / 100,
      });
    }
  }

  // ===== Foyda ulushi tushdi — FOIZ PUNKTida =====
  // Nisbatlar nisbatini olish (`(m1-m0)/m0`) manfiy bazada ishorani
  // ag'darib yuborardi; backend ham aynan shu sababdan `abs(prev)` ga
  // bo'ladi. Shuning uchun bu yerda ayirma — punktlarda.
  if (hasProducts) {
    const prevRevenue = num(prev.total_revenue);
    if (curRevenue >= T.minRevenue && prevRevenue >= T.minRevenue) {
      const m1 = (num(cur.profit) / curRevenue) * 100;
      const m0 = (num(prev.profit) / prevRevenue) * 100;
      if (m0 - m1 >= T.marginDropPoints) {
        out.push({
          id: "profitMargin",
          severity: "warning",
          messageKey: "profitMargin",
          params: { now: m1.toFixed(1), prev: m0.toFixed(1) },
          // Tannarxsiz tovarda tannarx 0 — foyda oshiq ko'rinishi mumkin.
          hintKey: "profit",
          score: (m0 - m1) / 100,
        });
      }
    }
  }

  // ===== O'rtacha chek tushdi =====
  const avgPct = kpis.change_pct.avg_transaction;
  if (
    avgPct != null &&
    avgPct <= -T.avgCheckDropPct &&
    cur.services_count >= T.minWorksForAvgCheck &&
    prev.services_count >= T.minWorksForAvgCheck
  ) {
    out.push({
      id: "avgCheckDrop",
      severity: "warning",
      messageKey: "avgCheckDrop",
      params: { pct: Math.abs(avgPct).toFixed(0) },
      // Kassadagi alohida sotuvlar bu o'lchovga KIRMAYDI.
      hintKey: "avgTransaction",
      score: Math.abs(avgPct) / 100,
    });
  }

  // ===== Bitta xizmatga qaramlik =====
  // Maxraj `services_revenue` — `services[].revenue` bilan AYNI asos
  // (Σ Work.price), shuning uchun ulush aniq, taxminiy emas.
  const topService = analytics.services[0];
  if (topService && analytics.services.length >= 3 && servicesRevenue > 0) {
    const share = (num(topService.revenue) / servicesRevenue) * 100;
    if (share >= T.serviceDominancePct) {
      out.push({
        id: "serviceDominates",
        severity: "info",
        messageKey: "serviceDominates",
        params: { name: topService.name, pct: share.toFixed(0) },
        score: share / 100,
      });
    }
  }

  // ===== Bitta mijozga qaramlik (xuddi shu asos) =====
  const topClient = analytics.top_clients[0];
  if (topClient && topClient.works_count >= 3 && servicesRevenue > 0) {
    const share = (num(topClient.revenue) / servicesRevenue) * 100;
    if (share >= T.clientConcentrationPct) {
      out.push({
        id: "clientConcentration",
        severity: "info",
        messageKey: "clientConcentration",
        params: { name: topClient.full_name, pct: share.toFixed(0) },
        score: share / 100,
      });
    }
  }

  // ===== Bitta xodimga qaramlik =====
  const topEmployee = analytics.employees[0];
  if (
    topEmployee &&
    analytics.employees.length >= 3 &&
    topEmployee.share_pct >= T.employeeConcentrationPct
  ) {
    out.push({
      id: "employeeConcentration",
      severity: "info",
      messageKey: "employeeConcentration",
      params: { name: topEmployee.full_name, pct: topEmployee.share_pct.toFixed(0) },
      score: topEmployee.share_pct / 100,
    });
  }

  // ===== Yangi mijoz oqimi to'xtadi =====
  // "Bugun" davrida bir kun yangi mijozsiz o'tishi — normal hol, xabar emas.
  if (
    period !== "day" &&
    prev.new_clients >= T.minPrevNewClients &&
    cur.new_clients === 0
  ) {
    out.push({
      id: "newClientsStalled",
      severity: "warning",
      messageKey: "newClientsStalled",
      params: { prev: prev.new_clients },
      // Ustaxona-global: filial filtri bu songa ta'sir qilmaydi.
      hintKey: "newClients",
      score: Math.min(prev.new_clients / 20, 1),
    });
  }

  // ===== Eslatmalar yuborilmayapti =====
  const { sent, failed } = analytics.reminders;
  if (sent + failed >= T.minReminders && failed >= 3) {
    const failRate = (failed / (sent + failed)) * 100;
    if (failRate >= T.reminderFailPct) {
      out.push({
        id: "remindersFailed",
        severity: "danger",
        messageKey: "remindersFailed",
        params: { count: failed },
        score: failRate / 100,
      });
    }
  }

  return out
    .sort((a, b) => RANK[b.severity] - RANK[a.severity] || b.score - a.score)
    .slice(0, limit);
}
